import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import type { PlanInterval, SubscriptionTier } from '@/entities/subscription';
import { capitalizeTier } from '../model/capitalizeTier';
import { formatSubscriptionDate } from '../../model/formatSubscriptionDate';

type Props = {
  tier: SubscriptionTier;
  interval: PlanInterval | null;
  nextBillingDate: string | null;
  features: string[];
  meteredRate?: number;
  freeAllowance?: number;
  freeRosUnlimited?: boolean;
  priceMonthly?: number;
  onManageBilling?: () => void;
  onAddPaymentMethod?: () => void;
  manageBillingLoading?: boolean;
  addPaymentMethodLoading?: boolean;
};

const getIntervalChipKey = (interval: PlanInterval | null): string => {
  if (interval === 'year') {
    return 'subscription.status.annual';
  }
  if (interval === 'month') {
    return 'subscription.status.monthly';
  }
  return 'subscription.status.metered';
};

const getBillingIntervalLabelKey = (interval: PlanInterval | null): string => {
  if (interval === 'year') {
    return 'subscription.hero.billedAnnually';
  }
  return 'subscription.hero.billedMonthly';
};

export const PlanHeroCard = ({
  tier,
  interval,
  nextBillingDate,
  features,
  meteredRate,
  freeAllowance,
  freeRosUnlimited,
  priceMonthly,
  onManageBilling,
  onAddPaymentMethod,
  manageBillingLoading,
  addPaymentMethodLoading,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isFree = tier === 'free';
  const isElite = tier === 'elite';
  const formattedNextCharge = nextBillingDate ? formatSubscriptionDate(nextBillingDate, i18n.language) : '';

  const handleManageBillingClick = () => {
    onManageBilling?.();
  };

  const handleAddPaymentMethodClick = () => {
    onAddPaymentMethod?.();
  };

  return (
    <Card
      data-testid="plan-hero-card"
      data-tier={tier}
      variant="outlined"
      sx={{
        background: (theme) => {
          const isDark = theme.palette.mode === 'dark';
          const midShade = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
          const eliteAccent = isDark ? 'rgba(186, 104, 200, 0.12)' : 'rgba(156, 39, 176, 0.05)';
          return isElite
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${midShade} 50%, ${eliteAccent} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${midShade} 100%)`;
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 1 }}
            >
              {t('subscription.hero.yourCurrentPlan')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                {capitalizeTier(tier)}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                <Chip
                  label={t('subscription.status.active')}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'success.dark', borderColor: 'success.main' }}
                />
                {(isFree || interval !== null) && (
                  <Chip label={t(getIntervalChipKey(interval))} size="small" variant="outlined" />
                )}
              </Stack>
            </Box>

            {isFree ? (
              <Typography variant="body2" color="text.secondary">
                {freeRosUnlimited
                  ? t('subscription.hero.unlimitedRos')
                  : t('subscription.hero.payPerUse', {
                      amount: meteredRate ?? 0,
                      free: freeAllowance ?? 0,
                    })}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('subscription.hero.paidSubtitle', {
                  price: priceMonthly ?? 0,
                  interval: t(getBillingIntervalLabelKey(interval)),
                })}
                {formattedNextCharge ? ` · ${t('subscription.hero.nextCharge', { date: formattedNextCharge })}` : ''}
              </Typography>
            )}
          </Box>

          <Box>
            {isFree ? (
              <Button variant="outlined" onClick={handleAddPaymentMethodClick} disabled={addPaymentMethodLoading}>
                {t('subscription.hero.addPaymentMethod')}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleManageBillingClick} disabled={manageBillingLoading}>
                {t('subscription.manage.button')}
              </Button>
            )}
          </Box>
        </Box>

        {!isFree && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', display: 'block', mb: 2 }}
              >
                {t('subscription.features.includedOn', { plan: capitalizeTier(tier) })}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}
              >
                {features.map((featureKey) => (
                  <Box key={featureKey} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CheckIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
                    <Typography variant="body2">{t(featureKey)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
